import { TriggerNotificationPayload } from 'spindl-shared-components';

import { EmailTemplate, EmailTemplateStatus } from '../modules/emailTemplates/types';
import { BASE_URL, COMMUNICATION_PATH, COMMUNICATION_URL, IS_DEV, SERVICE_URL_TEMPLATE } from './config';
import { UNDERLINE_COLOURS, NOTIFICATION_DURATION } from './constants';
import { getTranslation } from './reactUtils';
import { UnderlineStyleEnum } from './types';

export const isEmail = (value: string) => {
  // eslint-disable-next-line
  const regexEmail = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regexEmail.test(value);
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const capitalizeFirstLetter = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const getUnderline = (index: number): UnderlineStyleEnum => {
  return UNDERLINE_COLOURS[index % UNDERLINE_COLOURS.length];
};

export const isEmptyString = (value?: string) => (value ? value.trim().length === 0 : true);

export const getLineCount = (code: string): number => {
  return code.split(/\r\n|\r|\n/).length;
};

export const filterEmailTemplates = (searchQuery: string, templates?: EmailTemplate[]): EmailTemplate[] => {
  if (!templates) {
    return [];
  }

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  return templates.filter((template) => template.name.trim().toLowerCase().includes(normalizedSearchQuery));
};

export const filterEmailStatus = (
  searchQuery: string, templates?: EmailTemplate[], statuses?: EmailTemplateStatus[],
): EmailTemplateStatus[] => {
  if (!templates || !statuses) {
    return [];
  }

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  return statuses.filter((status) => status.name.trim().toLowerCase().includes(normalizedSearchQuery));
};

const fallbackCopyTextToClipboard = (text: string, trigger?: (payload: TriggerNotificationPayload) => void) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  
  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand('copy');

    if (trigger) {
      trigger({
        message: getTranslation('side_bar.tags.copy_success'),
        type: 'Positive',
        duration: NOTIFICATION_DURATION,
      });
    }
  } catch {
    if (trigger) {
      trigger({
        message: getTranslation('side_bar.tags.copy_failed'),
        type: 'Critical',
        duration: NOTIFICATION_DURATION,
      });
    }
    console.warn(getTranslation('side_bar.tags.copy_failed'));
  } finally {
    document.body.removeChild(textArea);
  }
};

export const copyTextToClipboard = async (text: string, trigger?: (payload: TriggerNotificationPayload) => void) => {  
  if (!navigator.clipboard) {
    // Asynchronous Clipboard API not supported, fallback to document.execCommand('copy') which
    // is deprecated for modern browsers
    fallbackCopyTextToClipboard(text, trigger);
    return;
  }

  // Try to use the Asynchronous Clipboard API
  // Support at 91.46% as of May 6th, 2022: https://caniuse.com/async-clipboard
  try {
    await navigator.clipboard.writeText(text);

    if (trigger) {
      trigger({
        message: getTranslation('side_bar.tags.copy_success'),
        type: 'Positive',
        duration: NOTIFICATION_DURATION,
      });
    }
  } catch {
    if (trigger) {
      trigger({
        message: getTranslation('side_bar.tags.copy_failed'),
        type: 'Critical',
        duration: NOTIFICATION_DURATION,
      });
    }
    console.warn(getTranslation('side_bar.tags.copy_failed'));
  }
};

const extractUrlDetails = (url: string): {
  appId?: string;
  serviceName?: string;
} => {
  const serviceUrlRegexTemplate = (SERVICE_URL_TEMPLATE as string)
    .replace('{appId}', '([a-zA-Z0-9]*)')
    .replace('{serviceName}', '([a-zA-Z0-9]*)');

  const serviceUrlRegex = new RegExp(serviceUrlRegexTemplate);
  const results = serviceUrlRegex.exec(url);

  if (results) {
    const appId = results[1];
    const serviceName = results[2];
    return {
      appId,
      serviceName,
    };
  }
  return {};
};

export const getServerUrl = (): string => {
  if (IS_DEV) {
    return COMMUNICATION_URL;
  }

  const { appId } = extractUrlDetails(window.location.pathname);
  return `${BASE_URL}/${appId}/${COMMUNICATION_PATH}`;
};
